/**
 * Universal Navigation Bar Component
 *
 * Keyboard-aware fixed footer that floats above mobile keyboards.
 * Uses visualViewport API via CSS variable --kb for dynamic positioning.
 * Senior-friendly design with large touch targets and clear labels.
 */

import React from 'react'
import { useKeyboardInset } from '../hooks/useKeyboardInset'

interface NavigationBarProps {
  step: number
  totalSteps: number
  canProceed: boolean
  onBack: () => void
  onNext: () => void
  isFirstStep?: boolean
  isLastStep?: boolean
}

export default function NavigationBar({
  step,
  totalSteps,
  canProceed,
  onBack,
  onNext,
  isFirstStep = false,
  isLastStep = false
}: NavigationBarProps) {

  // Enable keyboard-aware positioning
  useKeyboardInset();

  return (
    <div
      className="navigation-bar"
      data-navigation-bar
      role="toolbar"
      aria-label="Step navigation controls"
    >
      <div className="flex gap-3 max-w-md mx-auto">
        {/* Back Button */}
        <button
          onClick={onBack}
          disabled={isFirstStep}
          className={`rounded-xl font-semibold transition-all duration-200 h-fluid-btn-lg text-fluid-base ${
            isFirstStep
              ? 'bg-gray-100 text-gray-300 cursor-not-allowed border-2 border-gray-100'
              : 'bg-white border-2 border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
          }`}
          style={{
            width: 'clamp(80px, 25%, 120px)',
          }}
        >
          ← Back
        </button>

        {/* Next Button */}
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`flex-1 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 h-fluid-btn-lg text-fluid-md ${
            canProceed
              ? 'bg-brand-green text-white hover:bg-brand-green-hover shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <span>{isLastStep ? 'Complete' : 'Next'}</span>
          {canProceed && !isLastStep && (
            <svg
              width="18"
              height="14"
              viewBox="0 0 18 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-white"
            >
              <path
                d="M11 1L17 7L11 13M17 7H1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}
